import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faQuestionCircle, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';

const PlanDeNegocioPage = () => {
    const [businessPlan, setBusinessPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAyudaModal, setShowAyudaModal] = useState(false);
    const [formData, setFormData] = useState({
        tipoEmpresa: '',
        modeloAplicable: '',
        costoPlanNegocio: '',
        fechaEntrega: '',
        responsable: '',
        apoyosExternos: '',
        beneficios: ''
    });

    const ciemId = useSelector(state => state.ciem.ciemId);

    // Fetch business plan on component mount
    useEffect(() => {
        if (ciemId) {
            fetchBusinessPlan();
        } else {
            setLoading(false);
        }
    }, [ciemId]);

    const fetchBusinessPlan = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/businessPlanBase/${ciemId}`);
            // Handle nested data structure if needed
            const data = response.data.data || response.data;
            setBusinessPlan(data);
            setError(null);
        } catch (err) {
            setError('Error al cargar el plan de negocio: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleShowEditModal = () => {
        if (businessPlan) {
            // Format date for input field (remove time part if exists)
            const fechaEntrega = businessPlan.fechaEntrega
                ? businessPlan.fechaEntrega.split('T')[0]
                : '';

            setFormData({
                tipoEmpresa: businessPlan.tipoEmpresa || '',
                modeloAplicable: businessPlan.modeloAplicable || '',
                costoPlanNegocio: businessPlan.costoPlanNegocio || '',
                fechaEntrega: fechaEntrega,
                responsable: businessPlan.responsable || '',
                apoyosExternos: businessPlan.apoyosExternos || '',
                beneficios: businessPlan.beneficios || ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            tipoEmpresa: '',
            modeloAplicable: '',
            costoPlanNegocio: '',
            fechaEntrega: '',
            responsable: '',
            apoyosExternos: '',
            beneficios: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                id: businessPlan ? businessPlan.id : null,
                ciemId: ciemId,
                tipoEmpresa: formData.tipoEmpresa,
                modeloAplicable: formData.modeloAplicable,
                costoPlanNegocio: formData.costoPlanNegocio,
                fechaEntrega: formData.fechaEntrega ? formData.fechaEntrega + 'T00:00:00' : null,
                responsable: formData.responsable,
                apoyosExternos: formData.apoyosExternos,
                beneficios: formData.beneficios
            };

            await axiosInstance.post('/businessPlanBase/', submitData);
            handleCloseModal();
            fetchBusinessPlan();
        } catch (err) {
            setError('Error al actualizar el plan de negocio: ' + err.message);
        }
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para ver el plan de negocio.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-3">
                                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                        Plan de Negocio
                                    </h3>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setShowAyudaModal(true)}
                                        title="Ayuda"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                                        Ayuda
                                    </Button>
                                </div>
                                {businessPlan && (
                                    <Button variant="primary" onClick={handleShowEditModal}>
                                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                                        Editar
                                    </Button>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : businessPlan ? (
                                <Table responsive hover>
                                    <tbody>
                                        <tr>
                                            <th style={{ width: '30%' }}>Tipo de Empresa</th>
                                            <td>{businessPlan.tipoEmpresa || '-'}</td>
                                        </tr>
                                        <tr>
                                            <th>Modelo Aplicable</th>
                                            <td>{businessPlan.modeloAplicable || '-'}</td>
                                        </tr>
                                        <tr>
                                            <th>Costo del Plan de Negocio</th>
                                            <td>{businessPlan.costoPlanNegocio || '-'}</td>
                                        </tr>
                                        <tr>
                                            <th>Fecha de Entrega</th>
                                            <td>{businessPlan.fechaEntrega ? new Date(businessPlan.fechaEntrega).toLocaleDateString() : '-'}</td>
                                        </tr>
                                        <tr>
                                            <th>Responsable</th>
                                            <td>{businessPlan.responsable || '-'}</td>
                                        </tr>
                                        <tr>
                                            <th>Apoyos Externos</th>
                                            <td>{businessPlan.apoyosExternos || '-'}</td>
                                        </tr>
                                        <tr>
                                            <th>Beneficios</th>
                                            <td>{businessPlan.beneficios || '-'}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            ) : (
                                <Alert variant="info">
                                    No hay información del plan de negocio disponible.
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Editar Plan de Negocio</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Tipo de Empresa</Form.Label>
                            <Form.Control
                                type="text"
                                name="tipoEmpresa"
                                value={formData.tipoEmpresa}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Modelo Aplicable</Form.Label>
                            <Form.Control
                                type="text"
                                name="modeloAplicable"
                                value={formData.modeloAplicable}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Costo del Plan de Negocio</Form.Label>
                            <Form.Control
                                type="text"
                                name="costoPlanNegocio"
                                value={formData.costoPlanNegocio}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Fecha de Entrega</Form.Label>
                            <Form.Control
                                type="date"
                                name="fechaEntrega"
                                value={formData.fechaEntrega}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Responsable</Form.Label>
                            <Form.Control
                                type="text"
                                name="responsable"
                                value={formData.responsable}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Apoyos Externos</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="apoyosExternos"
                                value={formData.apoyosExternos}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Beneficios</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="beneficios"
                                value={formData.beneficios}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            Guardar
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Ayuda */}
            <Modal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ayuda</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        Defina los aspectos relevantes para hacer el plan de negocio, observando si está considerando instalar una empresa nueva, reorganizar una empresa operando o reabrir una empresa cerrada. Valore la oferta de metodologías disponibles para hacer el plan de negocio, algunos programas de apoyo al emprendimiento disponen de sus propios modelos. www.productivos.org tiene a su disposición un modelo aplicable a empresas nuevas, otro para valorar empresas operando y otro más para reorganizar o reabrir empresas cerradas, los cuales usted puede emplear y después copiar y pegar en otros modelos. Búsquelos en la página www.productivos.org o contacte a la organización para que le den acceso al que usted necesite.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAyudaModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default PlanDeNegocioPage;
